import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, Menu, X, ArrowRight } from "lucide-react";
import site from "@/seo/site.json";
import pages from "@/seo/pages.json";

const LINKS = [
  { label: "Declaration", href: "#manifesto", testid: "nav-link-manifesto" },
  { label: "Reach", href: "#reach", testid: "nav-link-reach" },
  { label: "Movement", href: "#launchpad", testid: "nav-link-launchpad" },
  { label: "Pitch Room", href: "#pitch-room", testid: "nav-link-pitch-room" },
  { label: "Crew", href: "#team", testid: "nav-link-team" },
  { label: "Trailer", href: "#trailer", testid: "nav-link-trailer" },
  { label: "FAQ", href: "#faq", testid: "nav-link-faq" },
  { label: "Contact", href: "#contact", testid: "nav-link-contact" },
];

export const scrollToId = (href) => {
  if (window.location.pathname.replace(/\/+$/, "") !== "") {
    window.location.assign(`/${href}`);
    return;
  }
  const offset = -(document.querySelector("header[data-testid='main-nav']")?.offsetHeight || 72);
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset, duration: 1.6 });
  } else {
    const el = document.querySelector(href);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY + offset, behavior: "smooth" });
  }
};

const AudienceLinks = ({ className, testidPrefix, onClick }) => {
  const { pathname } = useLocation();
  return (
    <div className={className} data-testid={`${testidPrefix}-audience-links`}>
      <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-slate-600 mr-1">For</span>
      {pages.map((p, i) => (
        <span key={p.slug} className="flex items-center gap-3">
          {i > 0 && <span className="text-slate-700">·</span>}
          <Link
            to={`/${p.slug}/`}
            onClick={onClick}
            data-testid={`${testidPrefix}-link-${p.slug}`}
            className={`font-mono text-[10px] tracking-[0.25em] uppercase transition-colors duration-300 hover:text-signal ${
              pathname.replace(/\/+$/, "") === `/${p.slug}` ? "text-signal" : "text-slate-400"
            }`}
          >
            {p.navLabel}
          </Link>
        </span>
      ))}
    </div>
  );
};

const MobileMenu = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [open]);

  const go = (href) => {
    onClose();
    setTimeout(() => scrollToId(href), 80);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="mobile-menu"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[95] bg-obsidian/95 backdrop-blur-xl flex flex-col px-6 pt-[calc(72px+env(safe-area-inset-top))] pb-[calc(1.5rem+env(safe-area-inset-bottom))] overflow-y-auto"
        >
          <div className="absolute inset-0 scanlines pointer-events-none" />
          <nav className="relative flex-1 flex flex-col justify-center gap-1" aria-label="Mobile">
            <AudienceLinks
              testidPrefix="mobile"
              onClick={onClose}
              className="flex items-center gap-3 flex-wrap pb-4 mb-2 border-b border-white/10"
            />
            {LINKS.map((l, i) => (
              <motion.button
                key={l.href}
                data-testid={`mobile-${l.testid}`}
                onClick={() => go(l.href)}
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-center justify-between text-left py-3 border-b border-white/5 group"
              >
                <span className="font-head font-extrabold tracking-tight text-3xl text-white group-active:text-signal">
                  {l.label}
                </span>
                <span className="font-mono text-[10px] tracking-[0.25em] text-slate-600">
                  0{i + 1}
                </span>
              </motion.button>
            ))}
          </nav>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="relative mt-8"
          >
            <button
              data-testid="mobile-menu-cta"
              onClick={() => go("#contact")}
              className="w-full flex items-center justify-center gap-3 bg-signal text-obsidian font-bold font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 signal-glow"
            >
              Get Early Access
              <ArrowRight size={14} />
            </button>
            <div className="mt-6 flex items-center justify-between">
              <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-slate-500">
                Signal live — {site.launchDateLabel}
              </span>
              <div className="flex items-center gap-4">
                {site.socials.slice(0, 5).map((s) => (
                  <a
                    key={s.id}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Launchpad TV on ${s.label}`}
                    data-testid={`mobile-social-${s.id}`}
                    className="opacity-70 active:opacity-100"
                  >
                    <img src={`/social/${s.id}.svg`} alt="" width={16} height={16} className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Timecode = () => {
  const [tc, setTc] = useState("00:00:00:00");
  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      const p = (n) => String(n).padStart(2, "0");
      const f = Math.floor((d.getMilliseconds() / 1000) * 24);
      setTc(`${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}:${p(f)}`);
    }, 42);
    return () => clearInterval(id);
  }, []);
  return <span data-testid="nav-timecode">{tc}</span>;
};

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
    <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-[96] transition-[background-color,border-color] duration-500 border-b pt-[env(safe-area-inset-top)] ${
        scrolled || menuOpen
          ? "bg-black/70 backdrop-blur-xl border-white/10"
          : "bg-transparent border-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between gap-3 sm:gap-6">
        <button
          data-testid="nav-brand"
          onClick={() => {
            setMenuOpen(false);
            scrollToId("#top");
          }}
          className="flex items-center gap-3 group shrink-0"
        >
          <img
            src="/logo-lockup.svg"
            alt="Launchpad TV"
            width={850}
            height={74}
            className="w-[140px] sm:w-[170px] h-auto object-contain drop-shadow-[0_0_12px_rgba(63,169,245,0.4)]"
          />
        </button>

        <div className="hidden 2xl:flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-slate-500 uppercase">
          <Radio size={12} className="text-signal" />
          <span>TC</span>
          <Timecode />
          <span className="text-slate-700">/</span>
          <span>CH 001</span>
        </div>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={l.testid}
              onClick={() => scrollToId(l.href)}
              className="font-mono text-[11px] tracking-[0.25em] uppercase text-slate-400 hover:text-white transition-colors duration-300 whitespace-nowrap"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            data-testid="nav-cta-button"
            onClick={() => {
              setMenuOpen(false);
              scrollToId("#contact");
            }}
            className="font-mono text-[10px] sm:text-[11px] tracking-[0.15em] sm:tracking-[0.2em] uppercase bg-signal text-obsidian font-bold px-3.5 sm:px-5 py-2.5 hover:bg-white transition-colors duration-300 signal-glow whitespace-nowrap"
          >
            <span className="sm:hidden">Early Access</span>
            <span className="hidden sm:inline">Get Early Access</span>
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className="lg:hidden flex items-center justify-center w-11 h-11 -mr-1 text-white glass-panel active:border-signal/60"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      <AudienceLinks
        testidPrefix="nav"
        className="hidden lg:flex items-center justify-end gap-3 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-7 -mt-2 pb-1"
      />
    </motion.header>
    </>
  );
}
